import { useEffect, useState } from "react";
import { Activity, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const STATUS_STYLE = {
  completed: "bg-emerald-100 text-emerald-700",
  failed: "bg-red-100 text-red-700",
  running: "bg-blue-100 text-blue-800",
  accepted: "bg-blue-100 text-blue-800",
  stale: "bg-amber-100 text-amber-800",
};

function fmtStats(stats) {
  if (!stats) return "";
  return Object.entries(stats)
    .slice(0, 4)
    .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`)
    .join(" · ");
}

function fmtWhen(iso) {
  if (!iso) return "Never run";
  const d = new Date(iso);
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  const ago = mins < 1 ? "just now" : mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.round(mins / 60)}h ago` : `${Math.round(mins / 1440)}d ago`;
  return `${d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} (${ago})`;
}

export function CronHealth() {
  const [crons, setCrons] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/admin/system/cron-runs");
      setCrons(data.crons);
    } catch {
      setCrons([]);
    } finally {
      setRefreshing(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="bg-white border border-border/60 rounded-xl p-6 mt-8" data-testid="cron-health-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-[#0B1B3D] flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#2563EB]" /> System health — scheduled jobs
        </h3>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={load} disabled={refreshing} data-testid="cron-health-refresh-btn">
          <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {!crons ? (
        <div className="space-y-3 animate-pulse">{[0, 1, 2, 3].map((i) => <div key={i} className="h-10 bg-slate-100 rounded" />)}</div>
      ) : (
        <div className="divide-y divide-border/50">
          {crons.map((c) => (
            <div key={c.name} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`cron-health-${c.name}`}>
              <div>
                <p className="text-sm font-semibold text-[#0B1B3D]">{c.label}</p>
                <p className="text-[11px] text-muted-foreground">{c.schedule} · {c.last_run ? fmtWhen(c.last_run.finished_at || c.last_run.created_at) : "Never run"}</p>
                {c.last_run?.stats && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 capitalize">{fmtStats(c.last_run.stats)}</p>
                )}
                {c.last_run?.status === "failed" && c.last_run?.error && (
                  <p className="text-[11px] text-red-600 mt-0.5">{c.last_run.error}</p>
                )}
              </div>
              <span
                className={`text-[11px] font-bold rounded-full px-3 py-1 ${STATUS_STYLE[c.last_run?.status] || "bg-slate-100 text-slate-500"}`}
                data-testid={`cron-status-${c.name}`}
              >
                {c.last_run?.status ? c.last_run.status.replace(/_/g, " ") : "no runs yet"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
